/**
 * Proactive Action Ingester (legacy helpers)
 *
 * Used for factual-digest snapshots (Help pipeline / support context) and optional
 * manual POSTs (e.g. HeaderBar test). Background Redux auto-ingest that queued
 * `proactive-ai-help` jobs was removed — proactive catch-up now runs only when the
 * user opens Agent Log (`useAgentLogProactiveSession` → `/proactive/agent-log-session`).
 *
 * Batches (when called) include:
 * - Factual digest (screen trail, API patterns, action counts) to limit context bloat
 * - Content references `{ type, _id }` from API JSON + compact data actions
 * - Context Settings (MCP toggles) and user context canvas weights
 */
import { Platform } from 'react-native';
import { HOST, joinHostWithApiPath } from '@api/constant';

const ACTIONS_TO_TRACK = [
	'NAVIGATING ON TAB',
	'OPENED DATA PAGE',
	'OPENED PROJECT',
	'DATA CHOSEN',
	'TOGGLED EASY MODE',
	'ADDED NEW DATA',
	'DATA PREVIEW REQUESTED',
];

const BATCH_INTERVAL_MS = 5000;
const ENDPOINT = '/api/ai/proactive-help/actions';

const MAX_SCREEN_TRAIL = 12;
const MAX_API_PATTERNS = 15;
const MAX_PAYLOAD_JSON = 320;
const MAX_CONTENT_REFS = 50;
const MAX_REF_EXTRACT_NODES = 400;

export type ProactiveContentRef = { type: string; _id: string; source?: string };

const TYPE_KEYS = [
	'type',
	'modelType',
	'kind',
	'docType',
	'entityType',
	'resourceType',
	'schemaName',
] as const;

/** Rolling factual digest (patterns persist across flushes; content refs cleared after each successful send). */
const digest = {
	actionTypeCounts: {} as Record<string, number>,
	screenTrail: [] as string[],
	apiPatterns: [] as string[],
	contentReferences: [] as ProactiveContentRef[],
};

type MetadataGetter = () => { userContextWeights?: Record<string, number> | null };

let getProactiveMetadata: MetadataGetter | null = null;

export function setProactiveAiMetadataGetter(fn: MetadataGetter | null) {
	getProactiveMetadata = fn;
}

function pushUniqueCap(arr: string[], val: string, max: number) {
	const v = val.trim();
	if (!v) return;
	const i = arr.indexOf(v);
	if (i >= 0) arr.splice(i, 1);
	arr.unshift(v);
	while (arr.length > max) arr.pop();
}

function simplifyApiPath(pathname: string): string {
	return pathname
		.replace(/\/[0-9a-f]{24}/gi, '/:id')
		.replace(/\/[0-9a-f-]{36}/gi, '/:uuid')
		.replace(/\/\d+/g, '/:n')
		.slice(0, 96);
}

function isRefId(s: string): boolean {
	if (typeof s !== 'string') return false;
	if (/^[0-9a-fA-F]{24}$/.test(s)) return true;
	return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
		s
	);
}

function normalizeRefType(raw: string): string {
	const t = String(raw)
		.trim()
		.replace(/[^a-zA-Z0-9_.-]/g, '_')
		.slice(0, 64);
	return t || 'record';
}

function inferTypeFromApiPath(pathname: string): string | null {
	const parts = pathname.split('/').filter(Boolean);
	for (let i = parts.length - 1; i >= 0; i--) {
		if (isRefId(parts[i]) && i > 0) {
			return normalizeRefType(parts[i - 1]);
		}
	}
	if (parts.length >= 2 && parts[0] === 'api') {
		return normalizeRefType(parts[parts.length - 1]);
	}
	return null;
}

function unwrapApiBody(body: unknown): unknown {
	if (body && typeof body === 'object' && (body as { success?: boolean }).success === true) {
		const d = (body as { data?: unknown }).data;
		if (d !== undefined) return d;
	}
	return body;
}

function pickTypeFromObject(obj: Record<string, unknown>, pathHint: string): string | null {
	for (const k of TYPE_KEYS) {
		const v = obj[k];
		if (typeof v === 'string' && v.trim()) return normalizeRefType(v.trim());
	}
	return inferTypeFromApiPath(pathHint);
}

function pushContentRef(ref: ProactiveContentRef) {
	const type = normalizeRefType(ref.type);
	const _id = String(ref._id).slice(0, 40);
	if (!type || !isRefId(_id)) return;
	const key = `${type}:${_id}`;
	const arr = digest.contentReferences;
	const i = arr.findIndex((r) => `${normalizeRefType(r.type)}:${String(r._id).slice(0, 40)}` === key);
	if (i >= 0) arr.splice(i, 1);
	arr.unshift({ type, _id, source: ref.source });
	while (arr.length > MAX_CONTENT_REFS) arr.pop();
}

function extractRefsFromValue(
	val: unknown,
	pathHint: string,
	depth: number,
	state: { nodes: number }
): void {
	if (state.nodes >= MAX_REF_EXTRACT_NODES) return;
	state.nodes += 1;
	if (depth > 14 || val == null) return;
	if (typeof val !== 'object') return;
	if (Array.isArray(val)) {
		for (const el of val) {
			extractRefsFromValue(el, pathHint, depth + 1, state);
			if (state.nodes >= MAX_REF_EXTRACT_NODES) return;
		}
		return;
	}
	const obj = val as Record<string, unknown>;
	const idRaw = obj._id;
	if (typeof idRaw === 'string' && isRefId(idRaw)) {
		const t = pickTypeFromObject(obj, pathHint);
		if (t) {
			const keyCount = Object.keys(obj).length;
			if (keyCount <= 48 || TYPE_KEYS.some((k) => typeof obj[k] === 'string')) {
				pushContentRef({ type: t, _id: idRaw, source: 'api' });
			}
		}
	}
	for (const k of Object.keys(obj)) {
		extractRefsFromValue(obj[k], pathHint, depth + 1, state);
		if (state.nodes >= MAX_REF_EXTRACT_NODES) return;
	}
}

/**
 * Walk JSON API responses and collect `{ type, _id }` pairs for proactive re-hydration (tier-1 refs).
 */
export function recordProactiveApiEntityRefs(
	config: { method?: string; url?: string; baseURL?: string },
	responseData?: unknown
): void {
	if (Platform.OS !== 'web' || !config?.url) return;
	try {
		const raw = config.baseURL
			? `${String(config.baseURL).replace(/\/+$/, '')}/${String(config.url).replace(/^\/+/, '')}`
			: String(config.url);
		const u = new URL(raw, 'https://placeholder.invalid');
		const pathHint = u.pathname;
		const unwrapped = unwrapApiBody(responseData);
		extractRefsFromValue(unwrapped, pathHint, 0, { nodes: 0 });
	} catch {
		/* optional */
	}
}

/**
 * Called from axios response interceptor — records anonymized API patterns (no query strings).
 */
export function recordProactiveHttpSample(config: {
	method?: string;
	url?: string;
	baseURL?: string;
}): void {
	if (Platform.OS !== 'web' || !config?.url) return;
	try {
		const method = (config.method || 'get').toUpperCase();
		const raw = config.baseURL
			? `${String(config.baseURL).replace(/\/+$/, '')}/${String(config.url).replace(/^\/+/, '')}`
			: config.url;
		const u = new URL(raw, 'https://placeholder.invalid');
		const path = simplifyApiPath(u.pathname);
		pushUniqueCap(digest.apiPatterns, `${method} ${path}`, MAX_API_PATTERNS);
	} catch {
		pushUniqueCap(
			digest.apiPatterns,
			`${config.method || 'GET'} ${String(config.url).slice(0, 80)}`,
			MAX_API_PATTERNS
		);
	}
}

/** Patterns + optional entity refs from response body (web proactive pipeline). */
export function recordProactiveApiResponse(
	config: { method?: string; url?: string; baseURL?: string },
	responseData?: unknown
): void {
	recordProactiveHttpSample(config);
	recordProactiveApiEntityRefs(config, responseData);
}

function routeNamesFromState(routes: any): string[] {
	const out: string[] = [];
	const walk = (r: any) => {
		if (!r) return;
		if (typeof r === 'string') {
			out.push(r);
			return;
		}
		if (Array.isArray(r)) {
			r.forEach(walk);
			return;
		}
		if (typeof r === 'object') {
			if (r.name) out.push(String(r.name));
			if (r.state) walk(r.state);
			if (Array.isArray(r.routes)) r.routes.forEach(walk);
		}
	};
	walk(routes);
	return out;
}

function tryEntityRefFromPayload(
	actionType: string,
	payload: any
): { type: string; _id: string } | null {
	if (!payload || typeof payload !== 'object') return null;
	const idRaw = payload._id ?? payload.dataId ?? payload.projectId ?? payload.id;
	const id = typeof idRaw === 'string' ? idRaw : null;
	if (!id || !isRefId(id)) return null;
	let typ: string | null = null;
	if (typeof payload.type === 'string') typ = normalizeRefType(payload.type);
	else if (typeof payload.modelType === 'string') typ = normalizeRefType(payload.modelType);
	else if (actionType === 'OPENED PROJECT') typ = 'Project';
	else if (actionType === 'OPENED DATA PAGE' || actionType === 'DATA CHOSEN') typ = 'Data';
	else if (actionType === 'ADDED NEW DATA') typ = 'Data';
	if (!typ) return null;
	return { type: typ, _id: id };
}

function extractNavScreen(payload: any, routes?: any): string | null {
	const p = payload;
	if (typeof p === 'string' && p.length) return p.slice(0, 120);
	if (Array.isArray(p) && p.length) {
		const last = p[p.length - 1];
		if (typeof last === 'string') return last.slice(0, 120);
		if (last && typeof last === 'object' && last.name) return String(last.name).slice(0, 120);
	}
	if (p && typeof p === 'object') {
		if (p.name) return String(p.name).slice(0, 120);
		if (p.routeName) return String(p.routeName).slice(0, 120);
	}
	if (routes) {
		const names = routeNamesFromState(routes);
		if (names.length) return names[names.length - 1].slice(0, 120);
	}
	return null;
}

function compactPayload(
	actionType: string,
	payload: any,
	extras?: { routes?: any }
): Record<string, unknown> {
	try {
		const entityRef = tryEntityRefFromPayload(actionType, payload);
		if (entityRef) {
			pushContentRef({ type: entityRef.type, _id: entityRef._id, source: 'action' });
			return { type: entityRef.type, _id: entityRef._id };
		}
		if (actionType === 'NAVIGATING ON TAB') {
			const screen = extractNavScreen(payload, extras?.routes);
			return screen ? { screen } : { nav: true };
		}
		if (payload == null) return {};
		if (typeof payload === 'string') return { v: payload.slice(0, 200) };
		if (typeof payload === 'number' || typeof payload === 'boolean') return { v: payload };
		if (Array.isArray(payload)) {
			return { n: payload.length, head: payload.slice(0, 3) };
		}
		if (typeof payload === 'object') {
			const keys = Object.keys(payload).slice(0, 8);
			const slim: Record<string, unknown> = {};
			for (const k of keys) {
				const val = (payload as any)[k];
				if (val == null) slim[k] = null;
				else if (typeof val === 'string') slim[k] = val.slice(0, 80);
				else if (typeof val === 'number' || typeof val === 'boolean') slim[k] = val;
				else if (Array.isArray(val)) slim[k] = `[${val.length}]`;
				else slim[k] = typeof val;
			}
			const s = JSON.stringify(slim);
			if (s.length <= MAX_PAYLOAD_JSON) return slim;
			return { keys: keys.join(',') };
		}
		return {};
	} catch {
		return {};
	}
}

function snapshotDigestForRequest() {
	return {
		actionTypeCounts: { ...digest.actionTypeCounts },
		screenTrail: [...digest.screenTrail],
		apiPatterns: [...digest.apiPatterns],
		contentReferences: [...digest.contentReferences],
	};
}

/** Public snapshot of the rolling factual digest (no content refs). */
export function getFactualDigestSnapshot(): {
	actionTypeCounts: Record<string, number>;
	screenTrail: string[];
	apiPatterns: string[];
} {
	return {
		actionTypeCounts: { ...digest.actionTypeCounts },
		screenTrail: [...digest.screenTrail],
		apiPatterns: [...digest.apiPatterns],
	};
}

let actionBuffer: Array<{ action: string; payload: Record<string, unknown> }> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

async function flush() {
	if (actionBuffer.length === 0) return;
	const toSend = [...actionBuffer];
	actionBuffer = [];
	flushTimer = null;

	try {
		const snapshot = snapshotDigestForRequest();
		const { contentReferences, ...factualDigest } = snapshot;
		const countKeys = Object.keys(factualDigest.actionTypeCounts);
		if (countKeys.length > 25) {
			const sorted = countKeys.sort(
				(a, b) =>
					(factualDigest.actionTypeCounts[b] || 0) -
					(factualDigest.actionTypeCounts[a] || 0)
			);
			const next: Record<string, number> = {};
			sorted.slice(0, 25).forEach((k) => {
				next[k] = factualDigest.actionTypeCounts[k] || 0;
			});
			factualDigest.actionTypeCounts = next;
		}

		let clientContextPreferences: Record<string, unknown> | undefined;
		let interactionSummaryExcerpt: string | undefined;
		let userContextWeights: Record<string, number> | null | undefined;

		try {
			const ctxMod = await import('@services/contextTracker');
			const settings = ctxMod.getContextSettings();
			clientContextPreferences = {
				userMcpToolsEnabled: settings.userMcpToolsEnabled !== false,
				attachInteractionSummaryToAiTools: !!settings.attachInteractionSummaryToAiTools,
				userInteractionFilter: settings.userInteractionFilter,
				trackUserInteractions: settings.trackUserInteractions,
				trackAIRequests: settings.trackAIRequests,
				trackTensorFlowRequests: settings.trackTensorFlowRequests,
			};
			if (Platform.OS === 'web') {
				try {
					userContextWeights = getProactiveMetadata?.()?.userContextWeights ?? undefined;
				} catch {
					userContextWeights = undefined;
				}
			}

			if (Platform.OS === 'web' && settings.attachInteractionSummaryToAiTools) {
				// Digest + weights already go as structured fields; sample adds live window + summary heads.
				interactionSummaryExcerpt = await ctxMod.getRollingContextSample({
					maxChars: 1800,
					includeDigest: false,
					includeWeights: false,
				});
			}
		} catch {
			/* optional */
		}

		const actions = toSend.map(({ action, payload }) => ({
			action,
			payload,
		}));

		const body: Record<string, unknown> = {
			actions,
			factualDigest,
		};
		if (contentReferences.length) body.contentReferences = contentReferences;
		if (clientContextPreferences) body.clientContextPreferences = clientContextPreferences;
		if (interactionSummaryExcerpt?.trim())
			body.interactionSummaryExcerpt = interactionSummaryExcerpt.trim();
		if (userContextWeights && Object.keys(userContextWeights).length)
			body.userContextWeights = userContextWeights;

		await fetch(joinHostWithApiPath(HOST, ENDPOINT), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify(body),
		});
		digest.contentReferences = [];
	} catch (err) {
		if (__DEV__) {
			console.warn('[ProactiveActionIngester] Failed to send actions:', err);
		}
	}
}

function scheduleFlush() {
	if (flushTimer) return;
	flushTimer = setTimeout(flush, BATCH_INTERVAL_MS);
}

export function ingestAction(
	actionType: string,
	payload?: any,
	extras?: { routes?: any }
) {
	if (!ACTIONS_TO_TRACK.includes(actionType)) return;

	digest.actionTypeCounts[actionType] = (digest.actionTypeCounts[actionType] || 0) + 1;
	if (actionType === 'NAVIGATING ON TAB') {
		const screen = extractNavScreen(payload, extras?.routes);
		if (screen) pushUniqueCap(digest.screenTrail, screen, MAX_SCREEN_TRAIL);
	}

	const compact = compactPayload(actionType, payload, extras);
	actionBuffer.push({ action: actionType, payload: compact });
	scheduleFlush();
}

export function subscribeToStore(store: {
	getState: () => any;
	subscribe: (cb: () => void) => () => void;
}) {
	return store.subscribe(() => {
		store.getState();
	});
}

export { ingestAction as recordActionForProactiveAI };
